import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { u3134FormatTaskListLabel, type U3134WorkInProgressSearchRow } from "../../../../u3134WorkInProgress/U3134Service";
import { NS, u3125FormatCurrency } from "../../../U3125";
import { isU3125WipLineHourly, u3125FormatWipEntryColumn, u3125FormatWipTransactionKindLabel } from "../../../U3125Service";

export type U3125WipTransactionTableVariant = "compact" | "full";

export interface U3125WipTransactionTableProps {
  rows: U3134WorkInProgressSearchRow[];
  variant?: U3125WipTransactionTableVariant;
  minWidthClass?: string;
  selectedIds?: Set<string>;
  onToggleOne?: (id: string, checked: boolean) => void;
  headerChecked?: boolean;
  headerIndeterminate?: boolean;
  onToggleHeader?: (checked: boolean) => void;
  /** Namespace i18n (défaut : u3125). Passer u3121 depuis le sommaire facturation projet. */
  translationNs?: string;
  /** Hauteur max. zone scroll du tableau (défaut : max-h-[55vh]). */
  scrollMaxHeightClass?: string;
}

function formatCellDate(value: unknown): string {
  if (value == null || value === "") return "";
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatEmployeeName(row: U3134WorkInProgressSearchRow): string {
  const first = row.employee?.firstName?.trim() ?? "";
  const last = row.employee?.lastName?.trim() ?? "";
  const full = `${first} ${last}`.trim();
  if (full) return full;
  return row.employee?.codeEmployee?.trim() ?? "";
}

function formatUnitRate(value: number | null | undefined): string {
  return u3125FormatCurrency(value);
}

function formatFactor(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return "";
  return String(value);
}

const U3125WipTransactionTable: React.FC<U3125WipTransactionTableProps> = ({
  rows,
  variant = "full",
  minWidthClass = "min-w-[1200px]",
  selectedIds,
  onToggleOne,
  headerChecked,
  headerIndeterminate,
  onToggleHeader,
  translationNs,
  scrollMaxHeightClass = "max-h-[55vh]",
}) => {
  const { t, i18n } = useTranslation();
  const i18nNs = translationNs ?? NS;
  const showCheckbox = selectedIds != null && onToggleOne != null;
  const showSellingColumns = useMemo(() => rows.some((r) => isU3125WipLineHourly(r)), [rows]);

  const amountTotals = useMemo(() => {
    let cost = 0;
    let selling = 0;
    for (const row of rows) {
      if (showCheckbox && selectedIds && !selectedIds.has(row.id)) continue;
      cost += Number(row.costAmount ?? 0);
      if (isU3125WipLineHourly(row)) {
        selling += Number(row.amount ?? 0);
      }
    }
    return {
      cost: Math.round(cost * 100) / 100,
      selling: Math.round(selling * 100) / 100,
    };
  }, [rows, selectedIds, showCheckbox]);

  const leadingColSpan = (variant === "compact" ? 4 : 6) + (showCheckbox ? 1 : 0);
  const taskColLabel = t("colTaskFt", { ns: "u3134" });
  const showFooter = rows.length > 0;
  const footerLabelKey = showCheckbox ? "lblWipSelectedTotals" : "lblSubtotal";

  return (
    <div className={`${scrollMaxHeightClass} overflow-auto rounded-lg border border-gray-200`}>
      <table className={`w-full ${minWidthClass} border-collapse text-left text-sm`}>
        <thead className="sticky top-0 z-10 bg-muted">
          <tr className="border-b border-gray-200">
            {showCheckbox ? (
              <th className="w-10 px-2 py-2 text-center align-middle" scope="col">
                {onToggleHeader ? (
                  <div className="flex justify-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0"
                      checked={headerChecked ?? false}
                      ref={(el) => {
                        if (el) el.indeterminate = headerIndeterminate ?? false;
                      }}
                      onChange={(e) => onToggleHeader(e.target.checked)}
                      aria-label={t("lblWipSelectAllHeader", { ns: i18nNs })}
                    />
                  </div>
                ) : (
                  <span className="sr-only">{t("lblWipSelectColumn", { ns: i18nNs })}</span>
                )}
              </th>
            ) : null}
            {variant === "compact" ? (
              <>
                <th className="px-3 py-2 font-medium">{t("colWipTransactionDate", { ns: i18nNs })}</th>
                <th className="px-3 py-2 font-medium">{taskColLabel}</th>
                <th className="px-3 py-2 font-medium">{t("colWipTransactionKind", { ns: i18nNs })}</th>
                <th className="px-3 py-2 font-medium">{t("colWipHonorariumEntryKind", { ns: i18nNs })}</th>
              </>
            ) : (
              <>
                <th className="px-3 py-2 font-medium">{t("colWipTransactionKind", { ns: i18nNs })}</th>
                <th className="px-3 py-2 font-medium">{t("colWipHonorariumEntryKind", { ns: i18nNs })}</th>
                <th className="px-3 py-2 font-medium">{t("colWipEmployeeName", { ns: i18nNs })}</th>
                <th className="px-3 py-2 font-medium">{t("colWipTransactionDate", { ns: i18nNs })}</th>
                <th className="px-3 py-2 font-medium">{taskColLabel}</th>
                <th className="px-3 py-2 font-medium text-right">{t("lblLineQuantity", { ns: i18nNs })}</th>
              </>
            )}
            <th className="px-3 py-2 font-medium text-right">{t("colWipCostRate", { ns: i18nNs })}</th>
            <th className="px-3 py-2 font-medium text-right">{t("colWipCostMarkup", { ns: i18nNs })}</th>
            <th className="px-3 py-2 font-medium text-right">{t("colWipCostAmount", { ns: i18nNs })}</th>
            {showSellingColumns ? (
              <>
                <th className="px-3 py-2 font-medium text-right">{t("colWipSellingRate", { ns: i18nNs })}</th>
                <th className="px-3 py-2 font-medium text-right">{t("colWipSellingMarkup", { ns: i18nNs })}</th>
                <th className="px-3 py-2 font-medium text-right">{t("colWipSellingAmount", { ns: i18nNs })}</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const hourly = isU3125WipLineHourly(row);
            const entryLabel = u3125FormatWipEntryColumn(row, t, i18nNs);
            const kindLabel = u3125FormatWipTransactionKindLabel(row.transactionKind, t, i18nNs);
            const taskLabel = u3134FormatTaskListLabel(row.task, i18n.language);
            return (
              <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50/80">
                {showCheckbox ? (
                  <td className="w-10 px-2 py-2 text-center align-middle">
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0"
                        checked={selectedIds!.has(row.id)}
                        onChange={(e) => onToggleOne!(row.id, e.target.checked)}
                        aria-label={t("lblWipSelectRow", { ns: i18nNs })}
                      />
                    </div>
                  </td>
                ) : null}
                {variant === "compact" ? (
                  <>
                    <td className="px-3 py-2">{formatCellDate(row.transactionDate)}</td>
                    <td className="max-w-[220px] truncate px-3 py-2" title={taskLabel || undefined}>
                      {taskLabel}
                    </td>
                    <td className="px-3 py-2">{kindLabel}</td>
                    <td className="max-w-[240px] truncate px-3 py-2" title={entryLabel}>
                      {entryLabel}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2">{kindLabel}</td>
                    <td className="max-w-[240px] truncate px-3 py-2" title={entryLabel}>
                      {entryLabel}
                    </td>
                    <td className="max-w-[200px] truncate px-3 py-2" title={formatEmployeeName(row)}>
                      {formatEmployeeName(row)}
                    </td>
                    <td className="px-3 py-2">{formatCellDate(row.transactionDate)}</td>
                    <td className="max-w-[220px] truncate px-3 py-2" title={taskLabel || undefined}>
                      {taskLabel}
                    </td>
                    <td className="px-3 py-2 text-right">{row.quantity != null ? String(row.quantity) : ""}</td>
                  </>
                )}
                <td className="px-3 py-2 text-right">{formatUnitRate(row.costRate)}</td>
                <td className="px-3 py-2 text-right">{formatFactor(row.costMarkupFactor)}</td>
                <td className="px-3 py-2 text-right">{u3125FormatCurrency(row.costAmount)}</td>
                {showSellingColumns ? (
                  <>
                    <td className="px-3 py-2 text-right">{hourly ? formatUnitRate(row.sellingRate) : ""}</td>
                    <td className="px-3 py-2 text-right">{hourly ? formatFactor(row.sellingMarkupFactor) : ""}</td>
                    <td className="px-3 py-2 text-right">{hourly ? u3125FormatCurrency(row.amount) : ""}</td>
                  </>
                ) : null}
              </tr>
            );
          })}
        </tbody>
        {showFooter ? (
          <tfoot className="sticky bottom-0 z-10 border-t-2 border-gray-300 bg-gray-100 font-medium">
            <tr>
              <td colSpan={leadingColSpan} className="px-3 py-2 text-right text-gray-700">
                {t(footerLabelKey, { ns: i18nNs })}
              </td>
              <td className="px-3 py-2" aria-hidden />
              <td className="px-3 py-2" aria-hidden />
              <td className="px-3 py-2 text-right">{u3125FormatCurrency(amountTotals.cost)}</td>
              {showSellingColumns ? (
                <>
                  <td className="px-3 py-2" aria-hidden />
                  <td className="px-3 py-2" aria-hidden />
                  <td className="px-3 py-2 text-right">{u3125FormatCurrency(amountTotals.selling)}</td>
                </>
              ) : null}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
};

export default U3125WipTransactionTable;
